import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getUserFullname, getUserType } from '../../services/api';
import userService from '../../services/userService';
import { useApp } from '../../context/AppContext';
import { useTheme } from '../../context/ThemeContext';
import { useCreateTicketModal } from '../../context/CreateTicketModalContext';
import ProfilePhotoModal from './ProfilePhotoModal';
import styled, { css, keyframes } from 'styled-components';
import {
  Camera,
  ChartColumn,
  ChevronRight,
  ExternalLink,
  House,
  Inbox,
  LoaderCircle,
  LogOut,
  Moon,
  Plus,
  Settings,
  Sun,
  Ticket,
} from 'lucide-react';

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const focusRing = css`
  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px var(--sb-accent);
  }
`;

const SidebarWrapper = styled.aside`
  --sb-bg: #0a0a0a;
  --sb-surface: #151515;
  --sb-surface-hover: rgba(255, 255, 255, 0.05);
  --sb-active-bg: rgba(255, 255, 255, 0.09);
  --sb-accent: #ffffff;
  --sb-text: #ededed;
  --sb-text-dim: #a1a1a1;
  --sb-text-muted: #8a8a8a;
  --sb-text-faint: #5c5c5c;
  --sb-border: rgba(255, 255, 255, 0.06);
  --sb-border-strong: rgba(255, 255, 255, 0.1);

  width: 260px;
  min-width: 260px;
  height: 100vh;
  background: var(--sb-bg);
  color: var(--sb-text);
  border-right: 1px solid var(--sb-border);
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 120;
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  @media (max-width: 768px) {
    transform: ${({ $open, $dragOffset }) => {
      if ($dragOffset != null) return `translateX(${$dragOffset}px)`;
      return $open ? 'translateX(0)' : 'translateX(-100%)';
    }};
    transition: ${({ $dragOffset }) =>
      $dragOffset != null ? 'none' : 'transform 0.25s ease, box-shadow 0.25s ease'};
    box-shadow: ${({ $open, $dragOffset }) => {
      const visible =
        $dragOffset != null ? $dragOffset > -250 : $open;
      return visible ? '8px 0 32px rgba(0, 0, 0, 0.45)' : 'none';
    }};
    touch-action: pan-y;
  }
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 1.25rem 1.25rem 1rem;
`;

const BrandMark = styled.img`
  width: 32px;
  height: 32px;
  display: block;
  flex-shrink: 0;
  border-radius: 50%;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.12);
`;

const BrandName = styled.span`
  font-size: 1rem;
  font-weight: 650;
  letter-spacing: -0.01em;
  white-space: nowrap;
  color: var(--sb-text);
`;

const CreateTicketButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  min-height: 38px;
  margin-bottom: 0.625rem;
  padding: 0 1rem;
  border: none;
  border-radius: 8px;
  background: #ffffff;
  color: #0a0a0a;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  transition: background-color 0.15s ease, transform 0.1s ease;

  &:hover {
    background: #e5e5e5;
  }

  &:active {
    transform: scale(0.98);
  }

  &:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:active {
      transform: none;
    }
  }
`;

const Nav = styled.nav`
  flex: 1;
  min-height: 0;
  padding: 0.5rem 0 1rem;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--sb-border-strong) transparent;
`;

const SectionLabel = styled.span`
  display: block;
  padding: 1rem 1.5rem 0.4rem;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--sb-text-faint);
  user-select: none;
`;

const NavLink = styled(Link)`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  min-height: 36px;
  margin: 1px 12px;
  padding: 0 0.75rem;
  border-radius: 8px;
  background: ${({ $active }) => ($active ? 'var(--sb-active-bg)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--sb-text)' : 'var(--sb-text-dim)')};
  font-size: 0.875rem;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  text-decoration: none;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:visited {
    color: ${({ $active }) => ($active ? 'var(--sb-text)' : 'var(--sb-text-dim)')};
  }

  &:hover {
    background: ${({ $active }) => ($active ? 'var(--sb-active-bg)' : 'var(--sb-surface-hover)')};
    color: var(--sb-text);
  }

  &::before {
    content: '';
    position: absolute;
    left: -12px;
    top: 8px;
    bottom: 8px;
    width: 3px;
    border-radius: 0 3px 3px 0;
    background: var(--sb-accent);
    opacity: ${({ $active }) => ($active ? 1 : 0)};
    transition: opacity 0.15s ease;
  }

  > svg:first-child {
    flex-shrink: 0;
    color: ${({ $active }) => ($active ? 'var(--sb-accent)' : 'currentColor')};
    transition: color 0.15s ease;
  }

  ${focusRing}
`;

const NavToggle = styled(NavLink).attrs({ as: 'button', type: 'button' })`
  width: calc(100% - 24px);
  border: none;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
`;

const NavLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Chevron = styled(ChevronRight)`
  flex-shrink: 0;
  color: var(--sb-text-faint);
  transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
  transition: transform 0.2s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const SubNavCollapse = styled.div`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  transition: grid-template-rows 0.22s ease, visibility 0.22s;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const SubNavInner = styled.div`
  overflow: hidden;
  min-height: 0;
`;

const SubNav = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin: 2px 12px 6px 32px;
  border-left: 1px solid var(--sb-border-strong);
`;

const SubNavLink = styled(Link)`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 32px;
  margin-left: 8px;
  padding: 0 0.65rem;
  border-radius: 7px;
  background: ${({ $active }) => ($active ? 'var(--sb-surface-hover)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--sb-text)' : 'var(--sb-text-muted)')};
  font-size: 0.8125rem;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  text-decoration: none;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:visited {
    color: ${({ $active }) => ($active ? 'var(--sb-text)' : 'var(--sb-text-muted)')};
  }

  &:hover {
    background: var(--sb-surface-hover);
    color: var(--sb-text);
  }

  &::before {
    content: '';
    position: absolute;
    left: -10px;
    top: 6px;
    bottom: 6px;
    width: 3px;
    border-radius: 3px;
    background: var(--sb-accent);
    opacity: ${({ $active }) => ($active ? 1 : 0)};
    transition: opacity 0.15s ease;
  }

  svg {
    flex-shrink: 0;
    color: var(--sb-text-faint);
  }

  ${focusRing}
`;

const SidebarFooter = styled.div`
  flex: 0 0 auto;
  padding: 0.75rem;
  border-top: 1px solid var(--sb-border);
`;

const UserCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.5rem;
  border-radius: 10px;
  background: var(--sb-surface);
  border: 1px solid var(--sb-border);
`;

const ProfileAvatarButton = styled.button`
  position: relative;
  width: 34px;
  height: 34px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  overflow: hidden;
  box-shadow: 0 0 0 1px var(--sb-border-strong);

  &:hover .avatar-overlay,
  &:focus-visible .avatar-overlay {
    opacity: 1;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px var(--sb-accent);
  }
`;

const ProfileAvatar = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const ProfileAvatarFallback = styled.span`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #262626;
  color: var(--sb-text);
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  user-select: none;
`;

const AvatarOverlay = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  opacity: 0;
  transition: opacity 0.15s ease;
  pointer-events: none;
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.25;

  strong {
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--sb-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    font-size: 0.6875rem;
    color: var(--sb-text-muted);
  }
`;

const IconButton = styled.button`
  width: 30px;
  height: 30px;
  padding: 0;
  border: none;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: transparent;
  color: var(--sb-text-muted);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--sb-surface-hover);
    color: var(--sb-text);
  }

  &:disabled {
    cursor: wait;
    opacity: 0.7;
  }

  .spin {
    animation: ${spin} 0.8s linear infinite;
  }

  ${focusRing}

  @media (prefers-reduced-motion: reduce) {
    .spin {
      animation-duration: 2s;
    }
  }
`;

const ICON_SIZE = 18;
const ICON_STROKE = 1.75;

const Sidebar = ({ open = false, dragOffset = null, onClose }) => {
  const location = useLocation();
  const { isDark, toggleDark } = useTheme();
  const { currentUser, fetchCurrentUser, addToast } = useApp();
  const { openCreateTicket } = useCreateTicketModal();
  const fullname = getUserFullname();
  const userType = getUserType();
  const isAdmin = userType === 'ADMIN';
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const welcomeName = fullname || 'Usuário';
  const roleLabel = isAdmin ? 'Administrador' : 'Usuário';
  const photoUrl = currentUser?.photoUrl;
  const avatarInitial = (welcomeName || 'U').trim().charAt(0) || 'U';
  const { pathname } = location;
  const isDashboardsRoute = pathname === '/dashboards' || pathname.startsWith('/dashboards/');
  const isTicketsRoute =
    pathname === '/all-tickets' ||
    pathname.startsWith('/all-tickets/') ||
    pathname.startsWith('/tickets/');
  const isSettingsRoute = pathname.startsWith('/admin');
  const [dashboardsOpen, setDashboardsOpen] = useState(isDashboardsRoute);
  const [ticketsOpen, setTicketsOpen] = useState(isTicketsRoute);

  useEffect(() => {
    if (isDashboardsRoute) setDashboardsOpen(true);
  }, [isDashboardsRoute]);

  useEffect(() => {
    if (isTicketsRoute) setTicketsOpen(true);
  }, [isTicketsRoute]);

  const dashboardsSections = useMemo(
    () => [
      { key: 'resolved-by-responsible', label: 'Responsáveis', to: '/dashboards/resolved-by-responsible' },
      {
        key: 'responsible-performance-over-time',
        label: 'Evolução responsáveis',
        to: '/dashboards/responsible-performance-over-time',
      },
      {
        key: 'support-resolution-breakdown',
        label: 'Motivos de resolução',
        to: '/dashboards/support-resolution-breakdown',
      },
      { key: 'total-by-app', label: 'Por aplicação', to: '/dashboards/total-by-app' },
      { key: 'total-by-created-user', label: 'Criadores', to: '/dashboards/total-by-created-user' },
      { key: 'tv', label: 'Painel TV', to: '/dashboards/tv', openInNewTab: true },
    ],
    []
  );

  const ticketsSections = useMemo(
    () => [
      { key: 'all', label: 'Todos', to: '/all-tickets' },
      { key: 'support', label: 'Suporte / Bugs', to: '/tickets/support' },
      { key: 'improvement', label: 'Melhorias', to: '/tickets/improvement' },
      { key: 'project', label: 'Projeto', to: '/tickets/project' },
      { key: 'other', label: 'Outras', to: '/tickets/other' },
    ],
    []
  );

  const handleLogout = () => {
    if (loggingOut) return;
    setLoggingOut(true);
    userService.logout();
  };

  const renderSubNav = (sections, isOpen, id) => (
    <SubNavCollapse $open={isOpen} id={id}>
      <SubNavInner>
        <SubNav>
          {sections.map((s) => {
            const active = !s.openInNewTab && pathname === s.to;
            return (
              <SubNavLink
                key={s.key}
                to={s.to}
                target={s.openInNewTab ? '_blank' : undefined}
                rel={s.openInNewTab ? 'noopener noreferrer' : undefined}
                title={s.openInNewTab ? 'Abre em nova aba' : undefined}
                $active={active}
                aria-current={active ? 'page' : undefined}
                onClick={onClose}
              >
                <NavLabel>{s.label}</NavLabel>
                {s.openInNewTab ? <ExternalLink size={13} strokeWidth={2} aria-hidden /> : null}
              </SubNavLink>
            );
          })}
        </SubNav>
      </SubNavInner>
    </SubNavCollapse>
  );

  const themeActionLabel = isDark ? 'Ativar tema claro' : 'Ativar tema escuro';

  return (
    <SidebarWrapper
      $open={open}
      $dragOffset={dragOffset}
      aria-hidden={!(open || (dragOffset != null && dragOffset > -252))}
    >
      <Brand>
        <BrandMark src="/supporthub-logo.png" alt="" aria-hidden />
        <BrandName>SupportHub</BrandName>
      </Brand>
      <Nav aria-label="Navegação principal">
        <SectionLabel>Geral</SectionLabel>
        <NavLink
          to="/home"
          $active={pathname === '/home'}
          aria-current={pathname === '/home' ? 'page' : undefined}
          onClick={onClose}
        >
          <House size={ICON_SIZE} strokeWidth={ICON_STROKE} aria-hidden />
          <NavLabel>Início</NavLabel>
        </NavLink>
        <NavLink
          to="/user/tickets"
          $active={pathname === '/user/tickets'}
          aria-current={pathname === '/user/tickets' ? 'page' : undefined}
          onClick={onClose}
        >
          <Ticket size={ICON_SIZE} strokeWidth={ICON_STROKE} aria-hidden />
          <NavLabel>Meus Tickets</NavLabel>
        </NavLink>

        {isAdmin && (
          <>
            <SectionLabel>Gestão</SectionLabel>
            <NavToggle
              $active={isDashboardsRoute}
              aria-expanded={dashboardsOpen}
              aria-controls="sidebar-dashboards-subnav"
              onClick={() => setDashboardsOpen((prev) => !prev)}
            >
              <ChartColumn size={ICON_SIZE} strokeWidth={ICON_STROKE} aria-hidden />
              <NavLabel>Dashboards</NavLabel>
              <Chevron size={15} strokeWidth={2} $open={dashboardsOpen} aria-hidden />
            </NavToggle>
            {renderSubNav(dashboardsSections, dashboardsOpen, 'sidebar-dashboards-subnav')}

            <NavToggle
              $active={isTicketsRoute}
              aria-expanded={ticketsOpen}
              aria-controls="sidebar-tickets-subnav"
              onClick={() => setTicketsOpen((prev) => !prev)}
            >
              <Inbox size={ICON_SIZE} strokeWidth={ICON_STROKE} aria-hidden />
              <NavLabel>Tickets</NavLabel>
              <Chevron size={15} strokeWidth={2} $open={ticketsOpen} aria-hidden />
            </NavToggle>
            {renderSubNav(ticketsSections, ticketsOpen, 'sidebar-tickets-subnav')}

            <NavLink
              to="/admin/settings"
              $active={isSettingsRoute}
              aria-current={isSettingsRoute ? 'page' : undefined}
              onClick={onClose}
            >
              <Settings size={ICON_SIZE} strokeWidth={ICON_STROKE} aria-hidden />
              <NavLabel>Configurações</NavLabel>
            </NavLink>
          </>
        )}
      </Nav>

      <SidebarFooter>
        <CreateTicketButton
          type="button"
          aria-haspopup="dialog"
          onClick={() => {
            onClose?.();
            openCreateTicket();
          }}
        >
          <Plus size={16} strokeWidth={2.25} aria-hidden />
          Criar ticket
        </CreateTicketButton>
        <UserCard>
          <ProfileAvatarButton
            type="button"
            onClick={() => setPhotoModalOpen(true)}
            title="Alterar foto de perfil"
            aria-label="Abrir editor de foto de perfil"
          >
            {photoUrl ? (
              <ProfileAvatar src={photoUrl} alt="" />
            ) : (
              <ProfileAvatarFallback aria-hidden="true">{avatarInitial}</ProfileAvatarFallback>
            )}
            <AvatarOverlay className="avatar-overlay">
              <Camera size={14} strokeWidth={2} aria-hidden />
            </AvatarOverlay>
          </ProfileAvatarButton>
          <UserInfo>
            <strong title={welcomeName}>{welcomeName}</strong>
            <span>{roleLabel}</span>
          </UserInfo>
          <IconButton
            type="button"
            onClick={toggleDark}
            title={themeActionLabel}
            aria-label={themeActionLabel}
          >
            {isDark ? (
              <Sun size={16} strokeWidth={ICON_STROKE} aria-hidden />
            ) : (
              <Moon size={16} strokeWidth={ICON_STROKE} aria-hidden />
            )}
          </IconButton>
          <IconButton
            type="button"
            disabled={loggingOut}
            onClick={handleLogout}
            title={loggingOut ? 'Saindo...' : 'Sair'}
            aria-label={loggingOut ? 'Saindo...' : 'Sair'}
          >
            {loggingOut ? (
              <LoaderCircle size={16} strokeWidth={ICON_STROKE} className="spin" aria-hidden />
            ) : (
              <LogOut size={16} strokeWidth={ICON_STROKE} aria-hidden />
            )}
          </IconButton>
        </UserCard>
      </SidebarFooter>

      <ProfilePhotoModal
        open={photoModalOpen}
        onClose={() => setPhotoModalOpen(false)}
        photoUrl={photoUrl}
        initialName={welcomeName}
        addToast={addToast}
        onSaved={fetchCurrentUser}
      />
    </SidebarWrapper>
  );
};

export default Sidebar;
